/**
 * Sticker IconLayer factory — plan 120, PR5.
 *
 * Pure builder that emits the Deck.gl IconLayer pair (icon + paired masked
 * shadow) for stickers. Shared by `useDeckOverlay` (visitor) and
 * `useAdminStickerLayer` (admin) so the editor previews exactly what
 * visitors see — including the opacity-only zoom math from primitives §4.
 *
 * The factory is intentionally Deck.gl-aware (constructs `IconLayer`
 * instances) but takes the class as an argument so the dynamic import in
 * each consumer stays the consumer's concern. No new bundle weight.
 */
import type { IconLayer as IconLayerType } from "@deck.gl/layers";
import {
  STICKER_DEFAULT_Z_ORDER,
  STICKER_SHADOW_DEFAULT_OPACITY,
  getLayerZOrder,
  stickerIconColor,
  stickerShadowColor,
  stickerShadowOffsetPx,
  type DeckIconMapping,
  type ThemeConfig,
} from "@wondermaps/maps";

/**
 * One sticker as the Deck.gl layer wants it. Visitor + admin map their
 * respective row shapes (`IllustrationWithAsset` / `IllustrationInstance`
 * + asset) into this struct via `toStickerFeature`.
 */
export interface StickerFeature {
  id: string;
  position: [number, number];
  /** Real-world width in meters; rendered with `sizeUnits: "meters"`. */
  widthMeters: number;
  /** CW degrees; converted to Deck's CCW convention via `getAngle`. */
  rotationDeg: number;
  /** Atlas slug — looked up against the iconMapping. */
  iconSlug: string;
  /** Per-instance base opacity (multiplied with the zoom-fade factor). */
  opacity: number;
  /** Effective zoom range — already resolved (per-instance or theme default in v2). */
  minZoom: number;
  maxZoom: number;
}

/** IconLayer constructor arg shape with the `beforeId` MapboxOverlay extension. */
type IconLayerArgs<D> = ConstructorParameters<typeof IconLayerType<D>>[0] & {
  beforeId?: string;
};

interface BuildStickerLayerArgs {
  IconLayerClass: typeof IconLayerType;
  features: StickerFeature[];
  atlas: { atlasUrl: string; mapping: Record<string, DeckIconMapping> };
  /** Live MapLibre zoom — drives the opacity ramp. */
  currentZoom: number;
  /** `false` on visitor (decoration only); `true` on admin (selection). */
  pickable: boolean;
  /** MapLibre `beforeId` for interleaved layer ordering. */
  beforeId?: string;
  /** Layer ID; admin and visitor pass distinct ids so both can co-exist. */
  id?: string;
  /** Optional autoHighlight + highlight color for admin selection feedback. */
  autoHighlight?: boolean;
  highlightColor?: [number, number, number, number];
  /** Extra updateTriggers keys (e.g. selection state) merged into getColor. */
  extraColorTriggers?: unknown[];
}

/**
 * Build the visible sticker IconLayer. `getColor` returns the alpha after
 * `zoomFactor` so out-of-window stickers fade smoothly instead of popping.
 */
export function buildStickerIconLayer({
  IconLayerClass,
  features,
  atlas,
  currentZoom,
  pickable,
  beforeId,
  id = "stickers",
  autoHighlight,
  highlightColor,
  extraColorTriggers = [],
}: BuildStickerLayerArgs): IconLayerType<StickerFeature> {
  const args: IconLayerArgs<StickerFeature> = {
    id,
    data: features,
    iconAtlas: atlas.atlasUrl,
    iconMapping: atlas.mapping,
    getIcon: (f: StickerFeature) => f.iconSlug,
    getPosition: (f: StickerFeature) => f.position,
    getSize: (f: StickerFeature) => f.widthMeters,
    sizeUnits: "meters",
    sizeMinPixels: 4,
    sizeMaxPixels: 1000,
    // Deck angle is CCW; data + slider are CW with 0 = artwork-up.
    getAngle: (f: StickerFeature) => -f.rotationDeg,
    getColor: (f: StickerFeature) =>
      stickerIconColor(f.opacity, currentZoom, f.minZoom, f.maxZoom),
    pickable,
    autoHighlight,
    ...(highlightColor !== undefined ? { highlightColor } : {}),
    // billboard:false keeps stickers stuck to the ground plane like tree
    // props, so they read as artwork "on" the map rather than 2D overlays
    // that float around with the camera. Plan §161 specifies this.
    billboard: false,
    // Disable depth test/write. Without this, the sticker quad's two
    // triangles get clipped by 3D building extrusions when the visitor
    // pitches the camera — half the quad fails the depth test and the
    // remaining half renders as a sharp triangular shard. (Tree props
    // don't hit this because they're 5–6m; stickers are 30–40m and span
    // building footprints.) Plan §157's `parameters: { depthTest: false }`
    // ported to luma.gl 9 syntax: `depthCompare: 'always'` skips the test,
    // `depthWriteEnabled: false` keeps the sticker from poisoning the
    // depth buffer for layers drawn after it.
    parameters: { depthCompare: "always", depthWriteEnabled: false },
    beforeId,
    updateTriggers: {
      getColor: [currentZoom, ...extraColorTriggers],
    },
  };
  return new IconLayerClass<StickerFeature>(args);
}

interface BuildStickerShadowLayerArgs {
  IconLayerClass: typeof IconLayerType;
  features: StickerFeature[];
  /** Masked variant of the atlas (mask: true on every entry → silhouette render). */
  shadowMapping: Record<string, DeckIconMapping>;
  atlasUrl: string;
  currentZoom: number;
  beforeId?: string;
  /** Default SE; theme-controlled in v2. */
  shadowDirection?: { x: number; y: number };
  shadowOpacity?: number;
  shadowOffsetMultiplier?: number;
  id?: string;
}

/**
 * Build the paired silhouette shadow IconLayer. Same data + size + angle as
 * the icon layer, mask:true on every mapping entry so the atlas texture is
 * ignored and the alpha drives a black silhouette. Slotted at the same
 * `beforeId` (sub-pixel offset is the only visual difference).
 */
export function buildStickerShadowLayer({
  IconLayerClass,
  features,
  shadowMapping,
  atlasUrl,
  currentZoom,
  beforeId,
  shadowDirection = { x: 1, y: 1 },
  shadowOpacity = STICKER_SHADOW_DEFAULT_OPACITY,
  shadowOffsetMultiplier = 1,
  id = "sticker-shadows",
}: BuildStickerShadowLayerArgs): IconLayerType<StickerFeature> {
  const args: IconLayerArgs<StickerFeature> = {
    id,
    data: features,
    iconAtlas: atlasUrl,
    iconMapping: shadowMapping,
    getIcon: (f: StickerFeature) => f.iconSlug,
    getPosition: (f: StickerFeature) => f.position,
    getSize: (f: StickerFeature) => f.widthMeters,
    sizeUnits: "meters",
    sizeMinPixels: 4,
    sizeMaxPixels: 1000,
    getAngle: (f: StickerFeature) => -f.rotationDeg,
    getPixelOffset: () => {
      const [ox, oy] = stickerShadowOffsetPx(currentZoom, shadowDirection);
      return [ox * shadowOffsetMultiplier, oy * shadowOffsetMultiplier];
    },
    getColor: (f: StickerFeature) =>
      stickerShadowColor(
        f.opacity,
        shadowOpacity,
        currentZoom,
        f.minZoom,
        f.maxZoom,
      ),
    pickable: false,
    billboard: false,
    // Same depth-test override as the icon layer — a clipped shadow
    // triangle on a pitched view looks just as broken as a clipped icon.
    parameters: { depthCompare: "always", depthWriteEnabled: false },
    beforeId,
    updateTriggers: {
      getColor: [currentZoom],
      getPixelOffset: [currentZoom],
    },
  };
  return new IconLayerClass<StickerFeature>(args);
}

/**
 * Resolve the MapLibre `beforeId` for inserting the sticker pair at the
 * z-order slot (default 7.7). Mirrors `findBeforeId(map, 7.75, theme)` in
 * `useDeckOverlay.ts:449` for props, with one important difference:
 *
 * **POI tier layers always win.** They're not in `LAYER_TYPE_Z_ORDER`
 * (and shouldn't be — they're map UI, not geometry), so the z-order
 * comparison would return the fallback 4 and let stickers paint on top.
 * That's wrong — POI markers + labels MUST sit above sticker artwork
 * (they carry wayfinding meaning; stickers are decoration only, plan
 * §25). Treating the first matching POI layer as the beforeId ensures
 * the sticker pair inserts BEFORE POIs in the MapLibre draw order →
 * POIs paint on top.
 */
const POI_LAYER_PREFIX = "pois-";

export function findStickerBeforeId(
  map: { getStyle: () => { layers?: { id: string }[] } | undefined },
  theme: ThemeConfig | undefined,
  targetZ: number = STICKER_DEFAULT_Z_ORDER,
): string | undefined {
  const styleLayers = map.getStyle()?.layers ?? [];
  for (const layer of styleLayers) {
    if (layer.id.startsWith(POI_LAYER_PREFIX)) return layer.id;
    const baseType = layer.id
      .replace(/-fill$/, "")
      .replace(/-line$/, "")
      .replace(/-symbol$/, "")
      .replace(/-shadow$/, "")
      .replace(/-extrusion$/, "");
    if (getLayerZOrder(baseType, theme) > targetZ) return layer.id;
  }
  return undefined;
}
